import React from "react";
import { motion } from "framer-motion";
import SEOHead from "@/components/SEOHead";

const ease = [0.16, 1, 0.3, 1];
const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-50px" },
  transition: { duration: 0.6, ease, delay },
});

const SECTIONS = [
  {
    title: "1. Acceptance of Terms",
    content: "By applying to and using ApplyStrat's services, you agree to be bound by these Terms of Service. If you do not agree, please do not use our services.",
  },
  {
    title: "2. Service Description",
    content: "ApplyStrat provides job application support services for tech candidates in the US market. Our service includes submitting applications on your behalf through official company portals, profile optimization, and strategic application support. We are not a staffing agency, recruiter, or employment agency.",
  },
  {
    title: "3. No Guarantee of Outcomes",
    content: "ApplyStrat does not guarantee job offers, interview callbacks, employment, or any specific hiring outcomes. We provide execution services only. Hiring decisions are made entirely by third-party employers and are outside our control.",
  },
  {
    title: "4. Refund Policy",
    content: "We offer a full refund if you are dissatisfied with the application strategy we are using for your profile. Refund requests must be made within 30 days of your start date. Refunds are based solely on strategy dissatisfaction — not on hiring outcomes, interview volume, or results.",
  },
  {
    title: "5. Client Responsibilities",
    content: "You are responsible for providing accurate information about your background, qualifications, and work authorization. You grant us permission to apply to roles on your behalf based on the preferences and criteria you provide. You must have valid US work authorization for all roles applied to.",
  },
  {
    title: "6. Confidentiality",
    content: "We maintain strict confidentiality of your personal information and application materials. We will not share your information with third parties beyond the official job applications we submit on your behalf.",
  },
  {
    title: "7. Limitation of Liability",
    content: "ApplyStrat's liability is limited to the fees you have paid for the current service period. We are not liable for any indirect, consequential, or incidental damages arising from our services or your participation in the job market.",
  },
  {
    title: "8. Termination",
    content: "Either party may terminate the service relationship at any time. You may cancel your plan at any time with no long-term obligation. We reserve the right to discontinue service if we determine that a continued relationship is not appropriate.",
  },
  {
    title: "9. Contact",
    content: "For any questions about these terms, please contact us at hello@applystrat.com.",
  },
];

const TermsPage = () => (
  <div data-testid="terms-page" className="min-h-screen bg-white">
    <SEOHead
      title="Terms of Service | ApplyStrat"
      description="Read ApplyStrat's Terms of Service. Understand what our tech job application support service includes, our refund policy, and client responsibilities."
      canonical="https://applystrat.com/terms"
    />

    <section className="pt-36 pb-28 max-w-3xl mx-auto px-6">
      <motion.div {...fadeUp()}>
        <p className="section-label-orange mb-4">Legal</p>
        <h1 className="text-4xl md:text-5xl font-medium tracking-tight text-ink mb-4 leading-tight">Terms of Service</h1>
        <p className="text-ink-secondary text-sm mb-12">Last updated: January 2025</p>
      </motion.div>

      <motion.div {...fadeUp(0.1)} className="space-y-4">
        {SECTIONS.map((section, i) => (
          <motion.div
            key={section.title}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-30px" }}
            transition={{ duration: 0.5, ease, delay: i * 0.05 }}
            className="bg-surface-raised rounded-2xl p-7 hover:bg-white hover:shadow-[0_4px_20px_rgb(0,0,0,0.06)] transition-all duration-300"
          >
            <h2 className="text-ink font-medium text-base mb-3">{section.title}</h2>
            <p className="text-ink-secondary text-sm leading-relaxed">{section.content}</p>
          </motion.div>
        ))}

        <motion.div {...fadeUp(0.3)} className="p-6 rounded-2xl bg-brand-light border border-brand/20">
          <p className="text-ink-secondary text-sm leading-relaxed">
            <strong className="text-brand">Note:</strong> This is a placeholder Terms of Service. We recommend consulting with a legal professional to create comprehensive terms tailored to your specific business operations and applicable laws.
          </p>
        </motion.div>
      </motion.div>
    </section>
  </div>
);

export default TermsPage;
